import {floodFill, shapeEnd} from './paint-engine';
import './paint.scss';

const WIDTH = 800, HEIGHT = 500, KEY = 'alex-paint';
const tools = [
  ['brush','Brush','B','<path d="m14 3 3 3-8 9-4-4zM5 12c-4 0-1 5-4 6 5 1 7-2 6-4"/>'],
  ['pencil','Pencil','P','<path d="m3 13 10-10 4 4L7 17l-5 1zM11 5l4 4"/>'],
  ['eraser','Eraser','E','<path d="m3 11 8-8 7 7-7 7H9zM7 7l7 7M9 17h9"/>'],
  ['fill','Fill','F','<path d="m7 3 9 9-7 6-7-7 7-7M3 11h12M17 13q5 6 0 6-3-1 0-6"/>'],
  ['picker','Eyedropper','I','<path d="m4 12 9-9 4 4-9 9-5 1zM10 4l6 6"/>'],
  ['line','Line','L','<path d="m3 17 14-14"/>'],
  ['rectangle','Rectangle','R','<rect x="3" y="4" width="14" height="12"/>'],
  ['ellipse','Ellipse','O','<ellipse cx="10" cy="10" rx="8" ry="6"/>'],
  ['text','Text','T','<path d="M3 5V3h14v2M10 3v14M7 17h6"/>'],
];
const palette = ['#000000','#ffffff','#6b7280','#9b4938','#e77329','#f3bc49','#f9e9b0','#406a43','#74a953','#83c5be','#3478ab','#343d78','#7b4f96','#d987aa','#8c6345','#c6a987'];
const svg = path => `<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" stroke-linecap="round" aria-hidden="true">${path}</svg>`;

class DesktopPaint extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    this.tool = 'brush'; this.history = []; this.future = []; this.revision = 0;
    this.innerHTML = `
      <div class="paint-filebar" role="group" aria-label="Drawing file">
        <button data-action="new">New</button><button data-action="open">Open image…</button><button data-action="save">Save PNG…</button>
        <span class="paint-file-separator"></span><button data-action="undo" disabled title="Undo (Ctrl/⌘ Z)">Undo</button><button data-action="redo" disabled title="Redo (Ctrl/⌘ Shift Z)">Redo</button>
        <input type="file" accept="image/png,image/jpeg,image/webp" data-file hidden>
      </div>
      <div class="paint-options">
        <label>Size <input type="range" min="1" max="40" value="5" aria-label="Stroke size"><output data-size>5 px</output></label>
        <label>Shapes <select data-shape aria-label="Shape style"><option value="outline">Outline</option><option value="solid">Solid</option></select></label>
        <label>Zoom <select data-zoom aria-label="Canvas zoom"><option value="fit">Fit</option><option value=".5">50%</option><option value="1">100%</option><option value="2">200%</option></select></label>
        <button data-action="flip">Flip horizontally</button>
      </div>
      <div class="paint-workspace">
        <div class="paint-toolbox" role="group" aria-label="Drawing tools">${tools.map(([id,name,key,path])=>`<button data-tool="${id}" aria-pressed="${id==='brush'}" title="${name} (${key})">${svg(path)}<span>${name}</span></button>`).join('')}</div>
        <div class="paint-paper" tabindex="0" aria-label="Drawing workspace"><canvas width="${WIDTH}" height="${HEIGHT}" tabindex="0" data-app-focus aria-label="Drawing canvas. Choose a tool and draw with mouse, pen or touch."></canvas></div>
      </div>
      <div class="paint-text-options" hidden><label>Text <input data-text maxlength="120" placeholder="Type, then click the canvas" aria-label="Text to place"></label><label>Size <select data-font-size aria-label="Text size"><option>16</option><option selected>24</option><option>36</option><option>48</option><option>72</option></select></label></div>
      <div class="paint-colors"><label>Color <input type="color" value="#e77329" aria-label="Ink color"></label><div role="group" aria-label="Color palette">${palette.map(color=>`<button data-color="${color}" aria-label="Use ${color}" title="${color}" style="--swatch:${color}"></button>`).join('')}</div></div>
      <div class="paint-statusbar"><span data-coordinates>800 × 500</span><span role="status">Saved on this device</span></div>`;
    this.canvas = this.querySelector('canvas');
    this.ctx = this.canvas.getContext('2d', {willReadFrequently:true});
    this.ctx.fillStyle = '#fff'; this.ctx.fillRect(0,0,WIDTH,HEIGHT);
    this.restore();
    const signal = this.abort.signal;
    this.addEventListener('click', e => {
      const tool = e.target.closest('[data-tool]');
      if (tool) this.selectTool(tool.dataset.tool);
      const color = e.target.closest('[data-color]');
      if (color) this.querySelector('[type=color]').value = color.dataset.color;
      const action = e.target.closest('[data-action]')?.dataset.action;
      if (action) this.action(action);
    }, {signal});
    this.querySelector('[type=range]').addEventListener('input', e => this.querySelector('[data-size]').textContent = `${e.target.value} px`, {signal});
    this.querySelector('[data-zoom]').addEventListener('change', () => this.zoom(), {signal});
    this.querySelector('[data-file]').addEventListener('change', e => this.openImage(e.target.files[0]), {signal});
    this.addEventListener('keydown', e => this.keydown(e), {signal});
    this.canvas.addEventListener('pointerdown', e => this.begin(e), {signal});
    this.canvas.addEventListener('pointermove', e => this.move(e), {signal});
    this.canvas.addEventListener('pointerup', e => this.end(e), {signal});
    this.canvas.addEventListener('pointercancel', () => this.cancel(), {signal});
    this.canvas.addEventListener('lostpointercapture', () => this.cancel(), {signal});
    window.addEventListener('blur', () => this.cancel(), {signal});
  }
  message(text) { this.querySelector('[role=status]').textContent = text; }
  image() { return this.ctx.getImageData(0,0,WIDTH,HEIGHT); }
  snapshot() {
    this.revision++;
    this.history.push(this.image());
    if (this.history.length > 20) this.history.shift();
    this.future = []; this.syncHistory();
  }
  syncHistory() {
    this.querySelector('[data-action=undo]').disabled = !this.history.length;
    this.querySelector('[data-action=redo]').disabled = !this.future.length;
  }
  persist() {
    try { localStorage.setItem(KEY,this.canvas.toDataURL()); this.message('Saved on this device'); }
    catch { this.message('Storage full — save a PNG to keep this drawing'); }
  }
  restore() {
    let stored; try { stored = localStorage.getItem(KEY); } catch { this.message('Local saving unavailable'); }
    if (!stored) return;
    const image = new Image(), revision = this.revision;
    image.onload = () => { if (this.isConnected && this.revision === revision) this.ctx.drawImage(image,0,0,WIDTH,HEIGHT); };
    image.onerror = () => this.message('Saved image could not load. Open an image or start drawing.');
    image.src = stored;
  }
  selectTool(tool) {
    this.cancel(); this.tool = tool;
    this.querySelectorAll('[data-tool]').forEach(button => button.setAttribute('aria-pressed',String(button.dataset.tool === tool)));
    this.querySelector('.paint-text-options').hidden = tool !== 'text';
    this.canvas.style.cursor = tool === 'text' ? 'text' : 'crosshair';
    this.message(tool === 'text' ? 'Type your text, then click to place it.' : ['rectangle','ellipse'].includes(tool) ? 'Drag to draw · Shift for equal sides' : tools.find(t=>t[0]===tool)[1]);
  }
  point(e) {
    const r = this.canvas.getBoundingClientRect();
    return {x:Math.max(0,Math.min(WIDTH-1,(e.clientX-r.left)*WIDTH/r.width)), y:Math.max(0,Math.min(HEIGHT-1,(e.clientY-r.top)*HEIGHT/r.height))};
  }
  ink() { return this.querySelector('[type=color]').value; }
  begin(e) {
    if (e.button !== 0 || this.drawing) return;
    e.preventDefault(); this.canvas.focus({preventScroll:true});
    const p = this.point(e);
    if (this.tool === 'picker') {
      const rgba = this.ctx.getImageData(Math.floor(p.x),Math.floor(p.y),1,1).data;
      this.querySelector('[type=color]').value = '#'+Array.from(rgba.slice(0,3)).map(v=>v.toString(16).padStart(2,'0')).join('');
      this.selectTool('brush'); return;
    }
    if (this.tool === 'text' && !this.querySelector('[data-text]').value.trim()) {
      this.querySelector('[data-text]').focus(); this.message('Enter text first.'); return;
    }
    this.snapshot();
    if (this.tool === 'fill') {
      const image = this.image(), hex = this.ink();
      floodFill(image,p.x,p.y,[1,3,5].map(i=>parseInt(hex.slice(i,i+2),16)).concat(255));
      this.ctx.putImageData(image,0,0); this.persist(); return;
    }
    if (this.tool === 'text') {
      this.ctx.fillStyle = this.ink(); this.ctx.font = `${this.querySelector('[data-font-size]').value}px Tahoma, sans-serif`;
      this.ctx.textBaseline = 'top'; this.ctx.fillText(this.querySelector('[data-text]').value,p.x,p.y); this.persist(); return;
    }
    this.drawing = true; this.pointer = e.pointerId; this.start = this.last = p;
    this.canvas.setPointerCapture(e.pointerId);
    if (['brush','pencil','eraser'].includes(this.tool)) this.stroke(p,p);
  }
  move(e) {
    const p = this.point(e);
    this.querySelector('[data-coordinates]').textContent = `${Math.round(p.x)}, ${Math.round(p.y)} · 800 × 500`;
    if (!this.drawing || e.pointerId !== this.pointer) return;
    if (['brush','pencil','eraser'].includes(this.tool)) this.stroke(this.last,p);
    else this.shape(p,e.shiftKey);
    this.last = p;
  }
  stroke(a,b) {
    const c = this.ctx, size = this.tool === 'pencil' ? 1 : Number(this.querySelector('[type=range]').value);
    c.strokeStyle = c.fillStyle = this.tool === 'eraser' ? '#ffffff' : this.ink();
    c.lineWidth = size; c.lineCap = 'round'; c.lineJoin = 'round';
    c.beginPath(); c.moveTo(a.x,a.y); c.lineTo(b.x,b.y); c.stroke();
    c.beginPath(); c.arc(b.x,b.y,size/2,0,Math.PI*2); c.fill();
  }
  shape(p, constrained) {
    const c = this.ctx, a = this.start, b = shapeEnd(a,p,constrained && this.tool !== 'line');
    c.putImageData(this.history.at(-1),0,0);
    c.strokeStyle = c.fillStyle = this.ink(); c.lineWidth = Number(this.querySelector('[type=range]').value);
    c.beginPath();
    if (this.tool === 'line') { c.moveTo(a.x,a.y); c.lineTo(b.x,b.y); }
    if (this.tool === 'rectangle') c.rect(a.x,a.y,b.x-a.x,b.y-a.y);
    if (this.tool === 'ellipse') c.ellipse((a.x+b.x)/2,(a.y+b.y)/2,Math.abs(b.x-a.x)/2,Math.abs(b.y-a.y)/2,0,0,Math.PI*2);
    if (this.tool !== 'line' && this.querySelector('[data-shape]').value === 'solid') c.fill();
    else c.stroke();
  }
  end(e) {
    if (!this.drawing || e.pointerId !== this.pointer) return;
    if (['line','rectangle','ellipse'].includes(this.tool)) this.shape(this.point(e),e.shiftKey);
    this.drawing = false;
    if (this.canvas.hasPointerCapture(e.pointerId)) this.canvas.releasePointerCapture(e.pointerId);
    this.persist();
  }
  cancel() {
    if (!this.drawing) return;
    this.drawing = false;
    this.ctx.putImageData(this.history.pop(),0,0); this.syncHistory();
    if (this.canvas.hasPointerCapture(this.pointer)) this.canvas.releasePointerCapture(this.pointer);
  }
  action(action) {
    this.cancel();
    if (action === 'undo' || action === 'redo') {
      const from = action === 'undo' ? this.history : this.future, to = action === 'undo' ? this.future : this.history;
      if (!from.length) return;
      this.revision++; to.push(this.image()); this.ctx.putImageData(from.pop(),0,0); this.syncHistory(); this.persist();
    }
    if (action === 'new') { this.snapshot(); this.ctx.fillStyle='#fff'; this.ctx.fillRect(0,0,WIDTH,HEIGHT); this.persist(); }
    if (action === 'flip') {
      this.snapshot(); const copy = document.createElement('canvas'); copy.width=WIDTH; copy.height=HEIGHT; copy.getContext('2d').drawImage(this.canvas,0,0);
      this.ctx.save(); this.ctx.translate(WIDTH,0); this.ctx.scale(-1,1); this.ctx.drawImage(copy,0,0); this.ctx.restore(); this.persist();
    }
    if (action === 'open') this.querySelector('[data-file]').click();
    if (action === 'save') {
      this.canvas.toBlob(blob => {
        if (!blob) { this.message('Export failed. Please try again.'); return; }
        const url=URL.createObjectURL(blob), a=document.createElement('a');
        a.download='Sketch.png'; a.href=url; document.body.append(a); a.click(); a.remove();
        setTimeout(()=>URL.revokeObjectURL(url),10000);
      },'image/png');
    }
  }
  async openImage(file) {
    if (!file) return;
    this.querySelector('[data-file]').value='';
    if (!['image/png','image/jpeg','image/webp'].includes(file.type) || file.size > 10*1024*1024) { this.message('Choose a PNG, JPEG or WebP under 10 MB.'); return; }
    const revision = this.revision, url = URL.createObjectURL(file), image = new Image();
    try {
      image.src=url; await image.decode();
      if (!this.isConnected || this.revision !== revision) return;
      if (image.naturalWidth*image.naturalHeight>20000000) { this.message('Choose an image under 20 megapixels.'); return; }
      this.snapshot(); this.ctx.fillStyle='#fff'; this.ctx.fillRect(0,0,WIDTH,HEIGHT);
      const scale=Math.min(WIDTH/image.naturalWidth,HEIGHT/image.naturalHeight);
      this.ctx.drawImage(image,(WIDTH-image.naturalWidth*scale)/2,(HEIGHT-image.naturalHeight*scale)/2,image.naturalWidth*scale,image.naturalHeight*scale);
      this.persist();
    } catch { this.message('That image could not be opened. Try a PNG or JPEG.'); }
    finally { URL.revokeObjectURL(url); }
  }
  zoom() {
    const value=this.querySelector('[data-zoom]').value;
    this.canvas.style.width=value==='fit'?'100%':`${WIDTH*Number(value)}px`;
    this.canvas.style.maxWidth=value==='fit'?'800px':'none';
  }
  keydown(e) {
    if (e.target.closest('input,select,textarea')) return;
    const key=e.key.toLowerCase();
    if ((e.metaKey||e.ctrlKey) && ['z','y','s'].includes(key)) {
      e.preventDefault(); e.stopPropagation();
      this.action(key==='s'?'save':key==='y'||e.shiftKey?'redo':'undo'); return;
    }
    if (e.metaKey||e.ctrlKey||e.altKey) return;
    if (key==='escape' && this.drawing) { e.preventDefault();e.stopPropagation();this.cancel();return; }
    const tool=tools.find(t=>t[2].toLowerCase()===key);
    if (tool) {e.preventDefault();this.selectTool(tool[0]);}
  }
  disconnectedCallback() { this.cancel(); this.abort.abort(); }
}
customElements.define('desktop-paint',DesktopPaint);
